import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SchedulesAPI } from "../api/schedules";
import type {
  Schedule,
  CreateScheduleRequest,
  UpdateScheduleRequest,
} from "../types";
import { useCurrentClient } from "@/features/clients/hooks/use-clients";
import { isDemoMode } from "@/features/utils/demo-mode";
import { mockSchedules } from "./use-schedules.mock";

// Query keys
export const schedulesKeys = {
  all: ["calendar_schedules"] as const,
  schedules: (clientId: string) =>
    [...schedulesKeys.all, "schedules", clientId] as const,
  schedule: (clientId: string, scheduleId: string) =>
    [...schedulesKeys.all, "schedules", clientId, scheduleId] as const,
};

// Query hooks
export function useSchedules() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<Schedule[]>({
    queryKey: schedulesKeys.schedules(clientId),
    queryFn: () => {
      if (isDemoMode()) {
        return Promise.resolve(mockSchedules);
      }
      return SchedulesAPI.listSchedules(clientId);
    },
    enabled: !!clientId,
    staleTime: 30 * 1000,
  });
}

export function useSchedule(scheduleId: string) {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  return useQuery<Schedule>({
    queryKey: schedulesKeys.schedule(clientId, scheduleId),
    queryFn: () => {
      if (isDemoMode()) {
        const schedule = mockSchedules.find((s) => s.id === scheduleId);
        return Promise.resolve(schedule!);
      }
      return SchedulesAPI.getSchedule(clientId, scheduleId);
    },
    enabled: !!clientId && !!scheduleId,
    staleTime: 30 * 1000,
  });
}

// Mutation hooks
export function useCreateSchedule() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateScheduleRequest) =>
      SchedulesAPI.createSchedule(clientId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: schedulesKeys.schedules(clientId),
      });
    },
  });
}

export function useUpdateSchedule() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      scheduleId,
      data,
    }: {
      scheduleId: string;
      data: UpdateScheduleRequest;
    }) => SchedulesAPI.updateSchedule(clientId, scheduleId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: schedulesKeys.schedules(clientId),
      });
      queryClient.invalidateQueries({
        queryKey: schedulesKeys.schedule(clientId, variables.scheduleId),
      });
    },
  });
}

export function useDeleteSchedule() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) =>
      SchedulesAPI.deleteSchedule(clientId, scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: schedulesKeys.schedules(clientId),
      });
    },
  });
}

export function useSetDefaultSchedule() {
  const currentClient = useCurrentClient();
  const clientId = currentClient?.client?.id || "";

  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scheduleId: string) =>
      SchedulesAPI.setDefaultSchedule(clientId, scheduleId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: schedulesKeys.schedules(clientId),
      });
    },
  });
}
